import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import type { LabourQuickRow } from '../../api/execution'
import { ErrorBlock } from '../../components/Feedback'

/** Labours, count, whose labour, hours, OT, remove. */
const GRID = '2.2fr 1fr 2.2fr 1fr 1fr 40px'

interface Option {
  id: string
  label: string
}

interface Row {
  key: number
  labourCategoryId: string
  /** Blank means our own workforce; otherwise the contractor who fielded them. */
  contractorId: string
  count: string
  workingHours: string
  overtimeHours: string
}

let nextKey = 1
const emptyRow = (contractorId = ''): Row => ({
  key: nextKey++,
  labourCategoryId: '',
  contractorId,
  count: '',
  workingHours: '',
  overtimeHours: '',
})

/**
 * Quick multi-trade entry: "Mason 10, Plumber 10, Helper 15".
 *
 * <p>Four things per line and nothing else. The skilled / semi-skilled / unskilled split
 * comes from the labour category master on the server, so an engineer standing on site is
 * not asked to fill a matrix for every trade.</p>
 *
 * <p>Whose labour it is rides on the same dropdown that names the contractor, because they
 * are one question - "own workforce" is simply the first option. Anything finer, such as
 * how many of a gang were women, belongs to the edit form where a line is corrected, not
 * to the form used standing in the sun with a muster roll.</p>
 */
export function LabourQuickForm({
  categories,
  contractors,
  pending,
  error,
  onCancel,
  onSave,
  inline = false,
}: {
  categories: Option[]
  contractors: Option[]
  pending: boolean
  error: unknown
  onCancel: () => void
  onSave: (rows: LabourQuickRow[]) => Promise<void>
  /** Embedded in a page rather than a dialog: no Cancel, and the grid clears after saving. */
  inline?: boolean
}) {
  const [rows, setRows] = useState<Row[]>([emptyRow(), emptyRow(), emptyRow()])

  function update(key: number, field: keyof Row, value: string) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)))
  }

  const filled = rows.filter((r) => r.labourCategoryId && Number(r.count) > 0)
  const totalWorkers = filled.reduce((sum, r) => sum + Number(r.count || 0), 0)
  const ownWorkers = filled
    .filter((r) => !r.contractorId)
    .reduce((sum, r) => sum + Number(r.count || 0), 0)
  // The same trade twice is a mistake; the same trade under two contractors is not.
  const duplicate =
    new Set(filled.map((r) => `${r.labourCategoryId}|${r.contractorId}`)).size !== filled.length

  // A new line usually belongs to whoever the last one did.
  const lastContractor = rows[rows.length - 1]?.contractorId ?? ''

  return (
    <>
      <DialogTitle>Labour deployed today</DialogTitle>
      <DialogContent dividers>
        {error != null && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock error={error} title="Could not save the labour" />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter a headcount per trade and say whose workers they were. The skill split is
          taken from the labour master, so a Mason counts as skilled and a Helper as
          unskilled without you saying so.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1, mb: 1 }}>
          {['Labours', 'Count', 'Whose labour', 'Hours', 'OT', ''].map((h) => (
            <Typography key={h} variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {h}
            </Typography>
          ))}
        </Box>

        {rows.map((row) => (
          <Box
            key={row.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 1,
              mb: 1,
              alignItems: 'center',
            }}
          >
            <TextField
              select
              value={row.labourCategoryId}
              onChange={(e) => update(row.key, 'labourCategoryId', e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              value={row.count}
              onChange={(e) => update(row.key, 'count', e.target.value)}
              slotProps={{ htmlInput: { min: 0 } }}
            />
            <TextField
              select
              value={row.contractorId}
              onChange={(e) => update(row.key, 'contractorId', e.target.value)}
            >
              <MenuItem value="">Own workforce</MenuItem>
              {contractors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              placeholder="8"
              value={row.workingHours}
              onChange={(e) => update(row.key, 'workingHours', e.target.value)}
            />
            <TextField
              type="number"
              placeholder="0"
              value={row.overtimeHours}
              onChange={(e) => update(row.key, 'overtimeHours', e.target.value)}
            />
            <IconButton
              size="small"
              onClick={() => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== row.key) : prev))}
              disabled={rows.length === 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setRows((p) => [...p, emptyRow(lastContractor)])}
        >
          Add trade
        </Button>

        {duplicate && (
          <Alert severity="error" sx={{ mt: 2 }}>
            The same trade appears twice for the same workforce. Combine them into one line.
          </Alert>
        )}
        {filled.length > 0 && !duplicate && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {filled.length} trade(s), {totalWorkers} workers on site — {ownWorkers} own,{' '}
            {totalWorkers - ownWorkers} contractor-supplied.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {!inline && <Button onClick={onCancel}>Cancel</Button>}
        <Button
          variant="contained"
          disabled={pending || filled.length === 0 || duplicate}
          onClick={async () => {
            await onSave(
              filled.map((r) => ({
                labourCategoryId: r.labourCategoryId,
                contractorId: r.contractorId || undefined,
                count: Number(r.count),
                workingHours: r.workingHours === '' ? undefined : Number(r.workingHours),
                overtimeHours: r.overtimeHours === '' ? undefined : Number(r.overtimeHours),
              })),
            )
            // Inline the grid stays on screen, so clear it ready for the next batch,
            // keeping whose labour it was - the next gang is usually the same.
            if (inline) {
              setRows([emptyRow(lastContractor), emptyRow(lastContractor), emptyRow(lastContractor)])
            }
          }}
        >
          {pending ? 'Saving...' : `Save ${filled.length || ''} line(s)`}
        </Button>
      </DialogActions>
    </>
  )
}
